import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * Reporting and blocking — App Store guideline 1.2 (24 Sep 2026).
 *
 * Both routes are open to any signed-in member. A report goes to the moderation
 * team; a block changes only what the caller sees — the member's posts and
 * comments leave every room, thread and the home feed. Neither tells the other
 * member anything.
 */
export const ReportReason = z.enum(['SPAM', 'MEDICAL_ADVICE', 'OFF_TOPIC', 'HARASSMENT', 'WORRIED', 'OTHER']);
/** The order a member reads them in: the most serious first, « Other » last. */
export const REPORT_REASONS = [
    'HARASSMENT',
    'WORRIED',
    'MEDICAL_ADVICE',
    'SPAM',
    'OFF_TOPIC',
    'OTHER',
];
/** The server's cap on `detail` (`FileReport`). */
export const REPORT_DETAIL_MAX = 1000;
/**
 * The body `POST /community/reports` takes: exactly one target, the reason,
 * and `detail` only when there is some. The server refuses an empty `detail`
 * (`min(1)`) and anything over 1000 characters, so a blank note is dropped
 * rather than sent and a long one is cut rather than refused.
 */
export function reportBody(target, reason, detail) {
    const note = detail?.trim().slice(0, REPORT_DETAIL_MAX).trim();
    return {
        ...('postId' in target ? { postId: target.postId } : { commentId: target.commentId }),
        reason,
        ...(note ? { detail: note } : {}),
    };
}
/**
 * `POST /community/reports`. Always answers `{ reported: true }`, a repeat
 * included, so the button needs no guard of its own.
 */
export function useReport(http) {
    return useMutation({
        mutationFn: ({ target, reason, detail }) => http.post('/community/reports', reportBody(target, reason, detail), {
            schema: z.object({ reported: z.literal(true) }),
        }),
    });
}
export const BlockedMember = z.object({
    userId: z.string().uuid(),
    fullName: z.string(),
    blockedAt: z.string().datetime(),
});
const BLOCKS_KEY = ['community', 'blocks'];
/**
 * Block or unblock. Every community read is refreshed after it — rooms,
 * threads, bookmarks and the home feed — so the member's posts leave (or come
 * back to) the screen straight away.
 */
export function useBlockMember(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, on }) => http.post(`/community/members/${userId}/block`, { on }, { schema: z.object({ blocked: z.boolean() }) }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['community'] });
            void queryClient.invalidateQueries({ queryKey: ['me', 'community-feed'] });
        },
    });
}
/** `GET /community/blocks` — the members the caller has blocked, most recent first. */
export function useBlockedMembers(http) {
    return useQuery({
        queryKey: BLOCKS_KEY,
        queryFn: () => http.get('/community/blocks', { schema: z.object({ items: z.array(BlockedMember) }) }),
    });
}
//# sourceMappingURL=safety.js.map